import styled from "@emotion/styled";
import { ErrorMessage } from "@hookform/error-message";
import { Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
`;

const HelperText = styled.span`
  font-size: 12px;
  color: ${({ theme, error }) =>
    error ? theme.palette.error.main : "inherit"};
`;

export const BankForm = ({ register, errors, watch }) => {
  const { t } = useTranslation();
  const [netSalary, setNetSalary] = useState(0);

  // Watch all relevant fields for calculation
  const grossSalary = watch("gross_salary") || 0;
  const overtime = watch("overtime") || 0;
  const tax = watch("tax") || 0;
  const transportAllowance = watch("transport_allowance") || 0;
  const credit = watch("credit") || 0;
  const houseAllowance = watch("house_allowance") || 0;
  const pension = watch("pension") || 0;
  const communicationAllowance = watch("communication_allowance") || 0;
  const providentFund = watch("provident_fund") || 0;
  const bonus = watch("bonus") || 0;
  const penalty = watch("penalty") || 0;

  useEffect(() => {
    // Convert all values to numbers
    const values = {
      grossSalary: Number(grossSalary),
      overtime: Number(overtime),
      tax: Number(tax),
      transportAllowance: Number(transportAllowance),
      credit: Number(credit),
      houseAllowance: Number(houseAllowance),
      pension: Number(pension),
      communicationAllowance: Number(communicationAllowance),
      providentFund: Number(providentFund),
      bonus: Number(bonus),
      penalty: Number(penalty)
    };

    // Calculate additions
    const additions =
      values.grossSalary +
      values.overtime +
      values.transportAllowance +
      values.houseAllowance +
      values.communicationAllowance +
      values.bonus;

    // Calculate deductions
    const deductions =
      values.tax +
      values.credit +
      values.pension +
      values.providentFund +
      values.penalty;

    // Calculate net salary and format with comma separators
    const calculatedNetSalary = additions - deductions;
    setNetSalary(
      calculatedNetSalary.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    );
  }, [
    grossSalary,
    overtime,
    tax,
    transportAllowance,
    credit,
    houseAllowance,
    pension,
    communicationAllowance,
    providentFund,
    bonus,
    penalty
  ]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2}>
        <Field>
          <Label htmlFor="salary_bank_name">
            {t("HR.BankandTin.Salary Bank Name")}
          </Label>
          <Input id="salary_bank_name" {...register("salary_bank_name")} />
          <ErrorMessage
            errors={errors}
            name="salary_bank_name"
            render={({ message }) => <HelperText error>{message}</HelperText>}
          />
        </Field>
        <Field>
          <Label htmlFor="salary_bank_account_number">
            {t("HR.BankandTin.SalaryBankAccount")}
          </Label>
          <Input
            id="salary_bank_account_number"
            {...register("salary_bank_account_number")}
          />
          <ErrorMessage
            errors={errors}
            name="salary_bank_account_number"
            render={({ message }) => <HelperText error>{message}</HelperText>}
          />
        </Field>
      </Stack>
      <Stack direction="row" spacing={2}>
        <Field>
          <Label htmlFor="pf_bank_name">{t("HR.BankandTin.PFBankName")}</Label>
          <Input id="pf_bank_name" {...register("pf_bank_name")} />
          <ErrorMessage
            errors={errors}
            name="pf_bank_name"
            render={({ message }) => <HelperText error>{message}</HelperText>}
          />
        </Field>
        <Field>
          <Label htmlFor="pf_bank_account_number">
            {t("HR.BankandTin.PFBankAccount")}
          </Label>
          <Input
            id="pf_bank_account_number"
            {...register("pf_bank_account_number")}
          />
          <ErrorMessage
            errors={errors}
            name="pf_bank_account_number"
            render={({ message }) => <HelperText error>{message}</HelperText>}
          />
        </Field>
      </Stack>
      <Field>
        <Label htmlFor="social_id_number">
          {t("HR.BankandTin.SocialIdentificationNumber")}
        </Label>
        <Input id="social_id_number" {...register("social_id_number")} />
        <ErrorMessage
          errors={errors}
          name="social_id_number"
          render={({ message }) => <HelperText error>{message}</HelperText>}
        />
      </Field>
      <Field>
        <Label htmlFor="tin_number">
          {t("HR.BankandTin.TaxIdentificationNumber")}
        </Label>
        <Input id="tin_number" {...register("tin_number")} />
        <ErrorMessage
          errors={errors}
          name="tin_number"
          render={({ message }) => <HelperText error>{message}</HelperText>}
        />
      </Field>
      <Field>
        <Label htmlFor="gross_salary">{t("HR.Compensation.GrossSalary")}</Label>
        <Input
          id="gross_salary"
          type="number"
          min="0"
          {...register("gross_salary")}
        />
        <ErrorMessage
          errors={errors}
          name="gross_salary"
          render={({ message }) => <HelperText error>{message}</HelperText>}
        />
      </Field>
      <Stack direction="row" spacing={2}>
        <Field>
          <Label htmlFor="overtime">{t("HR.Compensation.Overtime")}</Label>
          <Input
            id="overtime"
            type="number"
            min="0"
            {...register("overtime")}
          />
          <ErrorMessage
            errors={errors}
            name="overtime"
            render={({ message }) => <HelperText error>{message}</HelperText>}
          />
        </Field>
        <Field>
          <Label htmlFor="tax">{t("HR.Compensation.Tax")}</Label>
          <Input id="tax" type="number" min="0" {...register("tax")} />
          <ErrorMessage
            errors={errors}
            name="tax"
            render={({ message }) => <HelperText error>{message}</HelperText>}
          />
        </Field>
      </Stack>
      <Stack direction="row" spacing={2}>
        <Field>
          <Label htmlFor="transport_allowance">
            {t("HR.Compensation.TransportAllowance")}
          </Label>
          <Input
            id="transport_allowance"
            type="number"
            min="0"
            {...register("transport_allowance")}
          />
          <ErrorMessage
            errors={errors}
            name="transport_allowance"
            render={({ message }) => <HelperText error>{message}</HelperText>}
          />
        </Field>
        <Field>
          <Label htmlFor="credit">{t("HR.Compensation.Credit")}</Label>
          <Input id="credit" type="number" min="0" {...register("credit")} />
          <ErrorMessage
            errors={errors}
            name="credit"
            render={({ message }) => <HelperText error>{message}</HelperText>}
          />
        </Field>
      </Stack>
      <Stack direction="row" spacing={2}>
        <Field>
          <Label htmlFor="house_allowance">
            {t("HR.Compensation.HouseAllowance")}
          </Label>
          <Input
            id="house_allowance"
            type="number"
            min="0"
            {...register("house_allowance")}
          />
          <ErrorMessage
            errors={errors}
            name="house_allowance"
            render={({ message }) => <HelperText error>{message}</HelperText>}
          />
        </Field>
        <Field>
          <Label htmlFor="pension">{t("HR.Compensation.Pension")}</Label>
          <Input id="pension" type="number" min="0" {...register("pension")} />
          <ErrorMessage
            errors={errors}
            name="pension"
            render={({ message }) => <HelperText error>{message}</HelperText>}
          />
        </Field>
      </Stack>
      <Stack direction="row" spacing={2}>
        <Field>
          <Label htmlFor="communication_allowance">
            {t("HR.Compensation.CommunicationAllowance")}
          </Label>
          <Input
            id="communication_allowance"
            type="number"
            min="0"
            {...register("communication_allowance")}
          />
          <ErrorMessage
            errors={errors}
            name="communication_allowance"
            render={({ message }) => <HelperText error>{message}</HelperText>}
          />
        </Field>
        <Field>
          <Label htmlFor="provident_fund">
            {t("HR.Compensation.ProvidentFund")}
          </Label>
          <Input
            id="provident_fund"
            type="number"
            min="0"
            {...register("provident_fund")}
          />
          <ErrorMessage
            errors={errors}
            name="provident_fund"
            render={({ message }) => <HelperText error>{message}</HelperText>}
          />
        </Field>
      </Stack>
      <Stack direction="row" spacing={2}>
        <Field>
          <Label htmlFor="bonus">{t("HR.Compensation.Bonus")}</Label>
          <Input id="bonus" type="number" min="0" {...register("bonus")} />
          <ErrorMessage
            errors={errors}
            name="bonus"
            render={({ message }) => <HelperText error>{message}</HelperText>}
          />
        </Field>
        <Field>
          <Label htmlFor="penalty">{t("HR.Compensation.Penalty")}</Label>
          <Input id="penalty" type="number" min="0" {...register("penalty")} />
          <ErrorMessage
            errors={errors}
            name="penalty"
            render={({ message }) => <HelperText error>{message}</HelperText>}
          />
        </Field>
      </Stack>
      <Field>
        <Label htmlFor="net_salary">{t("HR.BankandTin.NetSalary")}</Label>
        <Input
          id="net_salary"
          value={netSalary}
          disabled
          style={{ textAlign: "right" }}
        />
      </Field>
    </Stack>
  );
};
